import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useUserStore } from '../../utils/userStore';
import { ROUTERS_PATH } from '../../routers/routers';
import { captureExerciseIcon } from '../../assets/images';
import { showInfoToast } from '../../components/Toaster';
import { ChatService } from '../../services/chat/chat.services';
import { TrackingService } from '../../services/tracking/tracking.service';
import { hebrewLetters } from '../../configs/config';
import { useExercisesHomeworkImageHandlers } from './useExercisesHomeworkImageHandlers';
import { isMobileDevice } from '../../utils/utils';
import { getPendingHomeworkUpload, clearPendingHomeworkUpload } from '../../utils/exerciseStore';

export enum HomeworkStep {
  GREETING = 'GREETING',
  AWAITING_CAPTURE = 'AWAITING_CAPTURE',
  IMAGE_RECEIVED = 'IMAGE_RECEIVED',
  QUESTIONS_DISPLAYED = 'QUESTIONS_DISPLAYED',
  QUESTION_SELECTED = 'QUESTION_SELECTED',
  SOLVING = 'SOLVING',
  SOLUTION_DISPLAYED = 'SOLUTION_DISPLAYED',
}

export interface HomeworkMessage {
  id: string;
  sender: 'bot' | 'user';
  text: string;
  type?: 'text' | 'options' | 'questions_list' | 'captured_image' | 'loader_bubble' | 'qr_code';
  imageUrl?: string;
  qrCodeUrl?: string;
  loadingText?: string;
  options?: {
    label: string;
    value: string;
    action?: () => void;
    icon?: React.ReactNode;
    color?: "black" | "dark" | "blue" | string;
    variant?: 'contained' | 'outlined';
  }[];
}

const chatService = new ChatService();
const trackingService = new TrackingService();

export function useExercisesHomework() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { userInfo } = useUserStore();

  const [localExerciseData, setLocalExerciseData] = useState<any>(location.state?.exerciseData || null);
  const [localCapturedImageUrl, setLocalCapturedImageUrl] = useState<string | null>(location.state?.capturedImageUrl || null);
  const studentName = userInfo?.first_name || '';
  
  // DYNAMIC GENDER SUFFIX FOR TRANSLATIONS
  const genderSuffix = userInfo?.gender === 'female' ? '_female' : '_male';

  const [currentStep, setCurrentStep] = useState<HomeworkStep>(
    location.state?.exerciseData || location.state?.pendingUpload || getPendingHomeworkUpload()
      ? HomeworkStep.IMAGE_RECEIVED
      : HomeworkStep.GREETING
  );
  const [messages, setMessages] = useState<HomeworkMessage[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [completedMessageIds, setCompletedMessageIds] = useState<Set<string>>(new Set());
  const [exerciseQuestions, setExerciseQuestions] = useState<any[]>([]);
  const [selectedQuestionId, setSelectedQuestionId] = useState<string | null>(null);
  const [selectedQuestionLetter, setSelectedQuestionLetter] = useState<string | null>(null);
  const [selectedQuestionText, setSelectedQuestionText] = useState<string | null>(null);
  const [solvedQuestionIds, setSolvedQuestionIds] = useState<Set<string>>(new Set());
  const [selectedQuestionIds, setSelectedQuestionIds] = useState<Set<string>>(new Set());

  const [isPolling, setIsPolling] = useState(false);
  const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
      }
    };
  }, []);

  const capturedImageBase64 = localExerciseData?.capture_image_b64 || null;

  // Ref to always access fresh completedMessageIds inside callbacks
  const completedMessageIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    completedMessageIdsRef.current = completedMessageIds;
  }, [completedMessageIds]);

  // Ref to keep track of messages
  const messagesRef = useRef<HomeworkMessage[]>([]);
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // ── Helpers ──

  const addBotMessage = useCallback((msg: Partial<HomeworkMessage>, delay = 500) => {
    const shouldShowTyping = !(msg.type === 'options' && !msg.text);
    if (shouldShowTyping) {
      setIsTyping(true);
    }
    const id = msg.id || (Date.now().toString() + Math.random());
    return new Promise<string>((resolve) => {
      setTimeout(() => {
        setMessages(prev => {
          if (prev.some(m => m.id === id)) return prev;
          return [...prev, {
            id,
            sender: 'bot',
            text: '',
            type: 'text',
            ...msg
          }];
        });
        if (shouldShowTyping) {
          setIsTyping(false);
        }
        resolve(id);
      }, delay);
    });
  }, []);

  const onMessageComplete = useCallback((id: string) => {
    setCompletedMessageIds(prev => new Set(prev).add(id));
  }, []);

  const { goToCapture, startManualCapture, isMobileActive } = useExercisesHomeworkImageHandlers({
    setMessages,
    addBotMessage,
    setLocalExerciseData,
    setLocalCapturedImageUrl,
    setCurrentStep,
    setIsPolling,
    pollingTimeoutRef,
    isMountedRef,
    genderSuffix,
  });

  const selectQuestionFromList = useCallback((question: any, letter: string) => {
    setSelectedQuestionId(question.id);
    setSelectedQuestionLetter(letter);
    setSelectedQuestionText(question.question);
    setSelectedQuestionIds(prev => {
      const next = new Set(prev);
      next.add(question.id);
      return next;
    });
    setCurrentStep(HomeworkStep.QUESTION_SELECTED);
  }, []);

  const solveInNotebook = useCallback(() => {
    showInfoToast(t(`underDevelopmentToast`));
  }, [t]);

  // ── Solution API ──

  const solveQuestion = useCallback(async () => {
    const questionToSolve = selectedQuestionText || 'פתור את התרגיל המופיע בתמונה';
    if (!capturedImageBase64 || !questionToSolve) return;

    setCurrentStep(HomeworkStep.SOLVING);

    const loaderId = 'solving-loader-' + Date.now();
    await addBotMessage({
      id: loaderId,
      type: 'loader_bubble',
      loadingText: t(`solvingQuestion`),
    }, 0);

    try {
      trackingService.trackEvent('homework_solve_clicked', {
        source: 'exercises_homework',
        selected_question: questionToSolve,
      });

      const response = await chatService.SOLVE_SPECIFIC_IMAGE_QUESTION({
        image: capturedImageBase64,
        question: questionToSolve,
      });

      setMessages(prev => prev.filter(m => m.id !== loaderId));

      await addBotMessage({
        id: `solution-${Date.now()}`,
        text: response.solution_content,
        type: 'text',
      });

      setCurrentStep(HomeworkStep.SOLUTION_DISPLAYED);

      // Track solved question ID
      const resolvedQuestionId = selectedQuestionId || 'single-question';
      const nextSolvedSet = new Set(solvedQuestionIds).add(resolvedQuestionId);
      setSolvedQuestionIds(nextSolvedSet);
      const allQuestionsSolved = exerciseQuestions.length === 0 || exerciseQuestions.every(q => nextSolvedSet.has(q.id));

      await addBotMessage({
        id: `post-prompt-${Date.now()}`,
        text: t(`hwFreeTextPostPrompt${genderSuffix}`, { name: studentName })
      }, 500);

      const options = [];
      if (!allQuestionsSolved) {
        options.push({
          label: t(`hwPickAnotherQuestion${genderSuffix}`),
          value: 'pick_another',
          color: 'black',
          variant: 'contained',
          action: () => {
            setCurrentStep(HomeworkStep.QUESTIONS_DISPLAYED);
            addBotMessage({
              id: `re-questions-prompt-${Date.now()}`,
              text: t(`hwNextQuestionPrompt${genderSuffix}`),
            }).then(() => {
              addBotMessage({ id: `re-questions-list-${Date.now()}`, type: 'questions_list' }, 0);
            });
          },
        });
      }
      options.push({
        label: t(`hwCaptureNewExercise${genderSuffix}`),
        value: 'new_capture',
        color: 'black',
        variant: allQuestionsSolved ? 'contained' : 'outlined',
        action: goToCapture,
      });

      await addBotMessage({
        id: `post-solution-options-${Date.now()}`,
        type: 'options',
        text: '',
        options: options,
      });
    } catch (error) {
      console.error('Solve question error:', error);
      setMessages(prev => prev.filter(m => m.id !== loaderId));
      await addBotMessage({
        id: `solve-error-${Date.now()}`,
        text: t(`hwConnectionError${genderSuffix}`),
        type: 'text',
      });
      setCurrentStep(HomeworkStep.QUESTION_SELECTED);
    }
  }, [capturedImageBase64, selectedQuestionText, addBotMessage, t, goToCapture, genderSuffix, chatService, trackingService, solvedQuestionIds, selectedQuestionId, exerciseQuestions, studentName]);

  const solveQuestionRef = useRef(solveQuestion);
  useEffect(() => {
    solveQuestionRef.current = solveQuestion;
  }, [solveQuestion]);

  // ── Reset Effect ──
  useEffect(() => {
    if (location.state?.reset) {
      setLocalExerciseData(null);
      setLocalCapturedImageUrl(null);
      setMessages([]);
      setCompletedMessageIds(new Set());
      setExerciseQuestions([]);
      setSelectedQuestionId(null);
      setSelectedQuestionLetter(null);
      setSelectedQuestionText(null);
      setSolvedQuestionIds(new Set());
      setSelectedQuestionIds(new Set());
      setIsTyping(false);
      setCurrentStep(HomeworkStep.GREETING);
      
      // Clear the reset flag from history state
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname]);

  // ── State Machine Effects ──

  useEffect(() => {
    if (currentStep !== HomeworkStep.GREETING) return;
    const init = async () => {
      await addBotMessage({
        id: 'greeting',
        text: t(`hwGreetingMessage${genderSuffix}`, { name: studentName }),
      });
    };
    init();
  }, [currentStep, studentName, t, addBotMessage, genderSuffix]);
  
  useEffect(() => {
    if (!completedMessageIds.has('greeting')) return;
    if (messages.some(m => m.id === 'capture-btn')) return;

    addBotMessage({
      id: 'capture-btn',
      type: 'options',
      text: '',
      options: [{
        label: t(`hwCaptureAction`),
        value: 'capture',
        color: 'black',
        variant: 'contained',
        icon: 'capture',
        action: goToCapture,
      }],
    });
    setCurrentStep(HomeworkStep.AWAITING_CAPTURE);
  }, [completedMessageIds, t, goToCapture, addBotMessage, messages]);

  const pendingCallbacksRef = useRef({ t, genderSuffix, addBotMessage, goToCapture });
  useEffect(() => {
    pendingCallbacksRef.current = { t, genderSuffix, addBotMessage, goToCapture };
  }, [t, genderSuffix, addBotMessage, goToCapture]);

  useEffect(() => {
    const pending = getPendingHomeworkUpload();
    if (!pending) return;

    let active = true;
    const loaderId = 'hw-analyzing-loader';
    const imageBubbleId = `captured-image-pending-${pending.sessionId}`;
    const getSteps = () => [
      pendingCallbacksRef.current.t('hwAnalyzingStep1'),
      pendingCallbacksRef.current.t('hwAnalyzingStep2'),
      pendingCallbacksRef.current.t('hwAnalyzingStep3'),
    ];
    let stepIndex = 0;

    setCurrentStep(HomeworkStep.IMAGE_RECEIVED);
    setLocalCapturedImageUrl(pending.previewUrl);

    setMessages(prev => {
      const filtered = prev.filter(m => m.id !== loaderId && m.id !== imageBubbleId);
      return [
        ...filtered,
        {
          id: imageBubbleId,
          sender: 'bot',
          text: '',
          type: 'captured_image',
          imageUrl: pending.previewUrl,
        },
        {
          id: loaderId,
          sender: 'bot',
          text: '',
          type: 'loader_bubble',
          loadingText: getSteps()[0],
        },
      ];
    });

    const intervalId = setInterval(() => {
      if (!active) return;
      const steps = getSteps();
      stepIndex = Math.min(stepIndex + 1, steps.length - 1);
      setMessages(prev =>
        prev.map(m =>
          m.id === loaderId
            ? { ...m, loadingText: steps[stepIndex] }
            : m
        )
      );
    }, 2500);

    pending.promise
      .then((response: any) => {
        clearInterval(intervalId);
        clearPendingHomeworkUpload();
        if (!active) return;

        const { t: translate, genderSuffix: gSuffix, addBotMessage: pushBotMsg, goToCapture: triggerCapture } = pendingCallbacksRef.current;
        setMessages(prev => prev.filter(m => m.id !== loaderId));

        if (!response) {
          pushBotMsg({
            id: `upload-error-${Date.now()}`,
            text: translate(`hwConnectionError${gSuffix}`),
          }).then(() => {
            if (!active) return;
            pushBotMsg({
              id: `retry-capture-btn-${Date.now()}`,
              type: 'options',
              text: '',
              options: [{
                label: translate(`hwRetryCapture${gSuffix}`),
                value: 'retry',
                color: 'black',
                variant: 'contained',
                icon: 'capture',
                action: triggerCapture,
              }],
            }, 0);
          });
          return;
        }

        if (response.is_math_exercise === false || response.status === 'invalid_image') {
          pushBotMsg({
            id: `no-questions-${Date.now()}`,
            text: translate(`hwNotAMathExercise${gSuffix}`),
          }).then(() => {
            if (!active) return;
            pushBotMsg({
              id: `retry-capture-btn-${Date.now()}`,
              type: 'options',
              text: '',
              options: [{
                label: translate(`hwRetryCapture${gSuffix}`),
                value: 'retry',
                color: 'black',
                variant: 'contained',
                icon: 'capture',
                action: triggerCapture,
              }],
            }, 0);
          });
          return;
        }

        if (response.exercise_id) {
          navigate(ROUTERS_PATH.DISPLAYEXERCISE, {
            state: {
              exerciseData: response,
              from: 'exercises-homework',
            },
          });
          return;
        }

        setMessages(prev => prev.filter(m => m.id !== imageBubbleId));
        setLocalExerciseData(response);
      })
      .catch((err: any) => {
        clearInterval(intervalId);
        clearPendingHomeworkUpload();
        console.error('Pending homework upload failed:', err);
        if (!active) return;
        const { t: translate, genderSuffix: gSuffix, addBotMessage: pushBotMsg, goToCapture: triggerCapture } = pendingCallbacksRef.current;
        setMessages(prev => prev.filter(m => m.id !== loaderId));
        pushBotMsg({
          id: `upload-error-${Date.now()}`,
          text: translate(`hwConnectionError${gSuffix}`),
        }).then(() => {
          if (!active) return;
          pushBotMsg({
            id: `retry-capture-btn-${Date.now()}`,
            type: 'options',
            text: '',
            options: [{
              label: translate(`hwRetryCapture${gSuffix}`),
              value: 'retry',
              color: 'black',
              variant: 'contained',
              icon: 'capture',
              action: triggerCapture,
            }],
          }, 0);
        });
      });

    return () => {
      active = false;
      clearInterval(intervalId);
    };
  }, [location.state, navigate]);

  const questionsLength = localExerciseData?.questions?.length || 0;
  const isMathExercise = localExerciseData?.is_math_exercise;
  const exerciseStatus = localExerciseData?.status;
  const captureKey = useMemo(
    () => (localExerciseData ? `${Date.now()}-${Math.random().toString(36).slice(2, 7)}` : ''),
    [localExerciseData]
  );

  useEffect(() => {
    if (currentStep !== HomeworkStep.IMAGE_RECEIVED) return;
    if (!localExerciseData || !captureKey) return;

    let active = true;

    const processCapture = async () => {
      // Reset previous exercise selection state when a new image arrives
      setSelectedQuestionId(null);
      setSelectedQuestionLetter(null);
      setSelectedQuestionText(null);
      setSolvedQuestionIds(new Set());
      setSelectedQuestionIds(new Set());

      const imageToShow = capturedImageBase64 || localCapturedImageUrl;
      if (imageToShow) {
        if (!active) return;
        await addBotMessage({
          id: `captured-image-${captureKey}`,
          type: 'captured_image',
          text: '',
          imageUrl: imageToShow,
        }, 0);
      }

      const questions = localExerciseData.questions || [];
      if (localExerciseData.is_math_exercise === false || localExerciseData.status === "invalid_image") {
        if (!active) return;
        const msgText = t(`hwNotAMathExercise${genderSuffix}`);
        await addBotMessage({
          id: `no-questions-${captureKey}`,
          text: msgText,
        });
        if (!active) return;
        await addBotMessage({
          id: `retry-capture-btn-${captureKey}`,
          type: 'options',
          text: '',
          options: [{
            label: t(`hwRetryCapture${genderSuffix}`),
            value: 'retry',
            color: 'black',
            variant: 'contained',
            icon: 'capture',
            action: goToCapture,
          }],
        }, 0);
        return;
      }

      if (questions.length === 0) {
        if (!active) return;
        const singleQId = `single-question-${captureKey}`;
        setExerciseQuestions([]);
        setSelectedQuestionId(singleQId);
        setSelectedQuestionLetter(null);
        setSelectedQuestionText('פתור את התרגיל המופיע בתמונה');
        setCurrentStep(HomeworkStep.QUESTION_SELECTED);

        await addBotMessage({
          id: `action-btns-${singleQId}`,
          type: 'options',
          text: '',
          options: [
            {
              label: t(`hwSolveForMe${genderSuffix}`),
              value: 'solve',
              color: 'black',
              variant: 'contained',
              icon: null,
              action: () => solveQuestionRef.current(),
            },
          ],
        }, 0);
        return;
      }

      const questionCards = questions.map((q: string, idx: number) => ({
        id: `q-${captureKey}-${idx}`,
        question: q,
      }));
      if (!active) return;
      setExerciseQuestions(questionCards);

      await addBotMessage({
        id: `questions-found-${captureKey}`,
        text: t(`hwFoundQuestions${genderSuffix}`, { count: questions.length }),
      });
    };

    processCapture();

    return () => {
      active = false;
    };
  }, [
    currentStep,
    captureKey,
    questionsLength,
    isMathExercise,
    exerciseStatus,
    capturedImageBase64,
    localCapturedImageUrl,
    addBotMessage,
    t,
    genderSuffix,
    goToCapture
  ]);

  useEffect(() => {
    if (!captureKey) return;
    const foundId = `questions-found-${captureKey}`;
    const listId = `questions-list-${captureKey}`;
    if (!completedMessageIds.has(foundId)) return;
    if (messages.some(m => m.id === listId)) return;

    addBotMessage({ id: listId, type: 'questions_list' }, 0);
    setCurrentStep(HomeworkStep.QUESTIONS_DISPLAYED);
  }, [captureKey, completedMessageIds, addBotMessage, messages]);

  useEffect(() => {
    if (currentStep !== HomeworkStep.QUESTION_SELECTED) return;
    if (!selectedQuestionLetter) return;

    const showActions = async () => {
      await addBotMessage({
        id: `question-selected-${selectedQuestionId}`,
        text: t(`hwQuestionSelected${genderSuffix}`, { letter: selectedQuestionLetter }),
      });
    };
    showActions();
  }, [currentStep, selectedQuestionId, selectedQuestionLetter, t, genderSuffix, addBotMessage]);

  useEffect(() => {
    const confirmId = `question-selected-${selectedQuestionId}`;
    if (!completedMessageIds.has(confirmId)) return;
    const actionsBtnId = `action-btns-${selectedQuestionId}`;
    if (messages.some(m => m.id === actionsBtnId)) return;

    addBotMessage({
      id: actionsBtnId,
      type: 'options',
      text: '',
      options: [
        /* {
          label: t(`hwSolveInNotebook${genderSuffix}`),
          value: 'notebook',
          color: 'black',
          variant: 'contained',
          icon: 'capture',
          action: solveInNotebook,
        }, */
        {
          label: t(`hwSolveForMe${genderSuffix}`),
          value: 'solve',
          color: 'black',
          variant: 'contained',
          icon: null,
          action: solveQuestion,
        },
      ],
    });
  }, [completedMessageIds, selectedQuestionId, t, genderSuffix, addBotMessage, messages, solveInNotebook, solveQuestion]);

  const addUserMessage = useCallback(async (text: string) => {
    if (!text.trim()) return;
    const id = `user-${Date.now()}`;
    setMessages(prev => [...prev, { id, sender: 'user', text, type: 'text' }]);

    setIsTyping(true);

    const questionsWithLetters = exerciseQuestions.map((q, idx) => {
      const letter = hebrewLetters[idx] || String(idx + 1);
      return `${t('section')} ${letter}: ${q.question}`;
    });

    const historyPayload = messagesRef.current
      .filter(m => m.type === 'text' && m.text)
      .map(m => ({
        role: m.sender === 'user' ? 'user' : 'assistant',
        content: m.text
      }));

    try {
      const response = await chatService.TALKWITHAGENT_V2({
        message: text,
        context: {
          current_page: 'exercises-homework',
          step_type: currentStep,
          metadata: {
            selected_question_id: selectedQuestionId,
            selected_question_letter: selectedQuestionLetter,
            selected_question_text: selectedQuestionText,
            exercise_content: localExerciseData?.exercise_content || null,
            questions: questionsWithLetters,
            gender: userInfo?.gender || 'neutral',
            is_desktop: !isMobileDevice(),
          }
        },
        history: historyPayload
      });

      if (response && response.message) {
        await addBotMessage({
          id: `bot-${Date.now()}`,
          text: response.message,
          type: 'text'
        }, 0);

        if (response.client_action && response.client_action.type === 'navigate' && response.client_action.route) {
          setTimeout(() => {
            navigate(response.client_action.route, { state: response.client_action.state });
          }, 1500);
          return;
        }

        if (response.client_action && response.client_action.type === 'show_capture_button') {
          await addBotMessage({
            id: `capture-btn-${Date.now()}`,
            type: 'options',
            text: '',
            options: [{
              label: t(`hwCaptureAction`),
              value: 'capture',
              color: 'black',
              variant: 'contained',
              icon: 'capture',
              action: goToCapture,
            }],
          }, 500);
          return;
        }

        if (response.client_action && response.client_action.type === 'solve_for_me') {
          solveQuestion();
          return;
        }

        if (localExerciseData) {
          const allQuestionsSolved = exerciseQuestions.length === 0 || exerciseQuestions.every(q => solvedQuestionIds.has(q.id));

          if (selectedQuestionId) {
            // Only show the post-prompt and buttons if they already got the solution
            if (currentStep === HomeworkStep.SOLUTION_DISPLAYED) {
              await addBotMessage({
                id: `post-prompt-${Date.now()}`,
                text: t(`hwFreeTextPostPrompt${genderSuffix}`, { name: studentName })
              }, 500);

              const options = [];
              if (!allQuestionsSolved) {
                options.push({
                  label: t(`hwPickAnotherQuestion${genderSuffix}`),
                  value: 'pick_another',
                  color: 'black',
                  variant: 'contained',
                  action: () => {
                    setCurrentStep(HomeworkStep.QUESTIONS_DISPLAYED);
                    addBotMessage({
                      id: `re-questions-prompt-${Date.now()}`,
                      text: t(`hwNextQuestionPrompt${genderSuffix}`),
                    }).then(() => {
                      addBotMessage({ id: `re-questions-list-${Date.now()}`, type: 'questions_list' }, 0);
                    });
                  },
                });
              }
              options.push({
                label: t(`hwCaptureNewExercise${genderSuffix}`),
                value: 'new_capture',
                color: 'black',
                variant: allQuestionsSolved ? 'contained' : 'outlined',
                action: goToCapture,
              });

              await addBotMessage({
                id: `post-free-text-options-${Date.now()}`,
                type: 'options',
                text: '',
                options: options,
              }, 0);
            }
          } else {
            // No question selected yet (general image context). Display post-prompt and Capture New Exercise button.
            await addBotMessage({
              id: `post-prompt-${Date.now()}`,
              text: t(`hwFreeTextPostPrompt${genderSuffix}`, { name: studentName })
            }, 500);

            await addBotMessage({
              id: `post-free-text-options-${Date.now()}`,
              type: 'options',
              text: '',
              options: [
                {
                  label: t(`hwCaptureNewExercise${genderSuffix}`),
                  value: 'new_capture',
                  color: 'black',
                  variant: 'outlined',
                  action: goToCapture,
                },
              ],
            }, 0);
          }
        }
      }
    } catch (error) {
      console.error('Agent chat error:', error);
      await addBotMessage({
        id: `error-${Date.now()}`,
        text: t(`hwConnectionError${genderSuffix}`),
        type: 'text'
      }, 0);
    } finally {
      setIsTyping(false);
    }
  }, [
    addBotMessage,
    t,
    genderSuffix,
    currentStep,
    selectedQuestionId,
    selectedQuestionLetter,
    selectedQuestionText,
    localExerciseData,
    exerciseQuestions,
    userInfo,
    studentName,
    goToCapture,
    solvedQuestionIds
  ]);

  return {
    messages,
    isTyping,
    onMessageComplete,
    completedMessageIds,
    exerciseQuestions,
    selectedQuestionId,
    selectQuestionFromList,
    addUserMessage,
    solvedQuestionIds,
    selectedQuestionIds,
    startManualCapture,
    isMobileActive
  };
}
